import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import { SUPPLIER_UNAVAILABLE_ERROR_CODE } from "../../data/models/mappers/mapError";

// Flag REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED (straks-lindring 14/9): the
// pure decisions of the "logo supplier is down" flow, kept out of the
// toasts, the API client and the editor view-models so they can be
// unit-tested without react-query, react-toastify or the router.
//
// Prod fact (14/9 19:47 DK): CreateAndUploadImageLogo ran 300 676 ms and
// answered 500 because app117's Upload_Base64 hung for the API's full 300 s
// ("The operation was canceled"); the App117Probe was already failing and
// the API's health latch was open. The user watched a spinning dialog for
// five minutes. The same synchronous supplier call sits under clone
// (CopyLogo), colour change, method change and resize.
//
// With the API's own flag on it answers those routes 503
// { code: "supplierUnavailable", retryAfterSeconds } straight from the
// health latch (0 supplier calls) while the supplier is down. On the FE:
//  - errorStatusFromBody keeps the marker as errorCode + retryAfterSeconds;
//  - withToasts shows ONE clear toast (supplier_unavailable) instead of the
//    compound "Could not … : <message>" and rethrows, so every caller's own
//    finally still stops its spinner and rolls back its optimistic state;
//  - the upload dialogs keep the chosen file (the customer-list dialog
//    stays open as today; the editor re-opens its confirmation with the
//    file kept) so one click retries;
//  - the upload requests get a client-side AbortController timeout
//    (REACT_APP_UPLOAD_CLIENT_TIMEOUT_MS, unset = none) - the safety net for
//    the case where the API itself does not answer; an abort surfaces as
//    the same error family and the same toast.
// Flag off = every helper here says "no" / passes the call through
// untouched: byte-identical DOM and wire.

export { SUPPLIER_UNAVAILABLE_ERROR_CODE };
// Marker of the client-side timeout (no HTTP status - the request was
// cancelled before any answer). Carried as ErrorStatus.errorCode with code 0.
export const UPLOAD_CLIENT_TIMEOUT_ERROR_CODE = "uploadClientTimeout";
// i18n key of the one toast (all 10 locale files).
export const SUPPLIER_UNAVAILABLE_TOAST_KEY = "supplier_unavailable";
// The toast stays a little longer than the default 5 s: it is the only
// explanation the user gets, and it asks them to wait minutes.
export const SUPPLIER_UNAVAILABLE_TOAST_MS = 10_000;

function mentionsSupplierUnavailable(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.toLowerCase().includes(SUPPLIER_UNAVAILABLE_ERROR_CODE.toLowerCase())
  );
}

/**
 * The flag-FREE shape test: the API's 503 supplierUnavailable (marker kept as
 * errorCode by errorStatusFromBody, or named in the message / raw JSON body
 * when the envelope carried no message) or the client-side upload timeout.
 * Shared by isSupplierUnavailableError (toast/dialog behaviour, flag
 * REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED) and the status banner's immediate
 * re-check (flag REACT_APP_SUPPLIER_STATUS_ENABLED), each gated by its own
 * flag at the call site.
 */
export function isSupplierUnavailableFamily(error: unknown): boolean {
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status) return false;
  if (status.errorCode === UPLOAD_CLIENT_TIMEOUT_ERROR_CODE) return true;
  if (status.code !== 503) return false;
  return (
    mentionsSupplierUnavailable(status.errorCode) ||
    mentionsSupplierUnavailable(status.message) ||
    mentionsSupplierUnavailable(error.message)
  );
}

/**
 * True for the API's 503 supplierUnavailable and for the client-side upload
 * timeout - the errors that get the one toast and keep the upload dialog's
 * file. Flag off = always false, whatever the error.
 */
export function isSupplierUnavailableError(error: unknown): boolean {
  if (!enviroment.supplierUnavailableEnabled) return false;
  return isSupplierUnavailableFamily(error);
}

/** The error an aborted upload request surfaces as (same family, same toast). */
export function uploadClientTimeoutError(timeoutMs: number): DataError {
  const seconds = Math.round(timeoutMs / 1000);
  const message = `The upload was cancelled after ${seconds} s without an answer from the API (client timeout)`;
  return new DataError(message, {
    code: 0,
    message,
    errorCode: UPLOAD_CLIENT_TIMEOUT_ERROR_CODE,
  });
}

/**
 * The client timeout in ms for the upload requests, or undefined for none:
 * only when the flag is on AND REACT_APP_UPLOAD_CLIENT_TIMEOUT_MS parsed to
 * a positive value. Flag off = undefined even with a value set, so the
 * flag alone decides whether the wire changes.
 */
export function uploadClientTimeoutMs(): number | undefined {
  if (!enviroment.supplierUnavailableEnabled) return undefined;
  const ms = enviroment.uploadClientTimeoutMs;
  return typeof ms === "number" && Number.isFinite(ms) && ms > 0
    ? ms
    : undefined;
}

/**
 * Runs an upload request under the client timeout. Without a timeout the
 * request is run exactly as before - `run` gets no signal, so the caller
 * builds today's RequestInit byte for byte. With one, an AbortController
 * is armed for the whole call (token acquisition + fetch + body read); an
 * abort surfaces as uploadClientTimeoutError, every other rejection
 * passes through untouched, and the timer is always cleared.
 */
export async function withUploadClientTimeout<T>(
  run: (signal?: AbortSignal) => Promise<T>
): Promise<T> {
  const ms = uploadClientTimeoutMs();
  if (ms === undefined) return run();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await run(controller.signal);
  } catch (error) {
    if (controller.signal.aborted) throw uploadClientTimeoutError(ms);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
